import type { TerritoryCurrency } from '../territory/territory.interface';
import type { ContractLine, ContractTotals } from './contract.interface';

/** Rounds an amount to the cent. */
export const roundToCents = (value: number): number => Math.round(value * 100) / 100;

/** Decimals each territory currency is invoiced in: the Lebanese pound has no minor unit in practice. */
export const CURRENCY_MINOR_UNITS: Record<TerritoryCurrency, number> = {
  EUR: 2,
  GBP: 2,
  LBP: 0,
  USD: 2,
};

/**
 * Rounds an amount half-up to its currency's minor unit (SWOOP-416): 1198.795 USD is 1198.8, 599.5 LBP is 600.
 * The decimal is shifted through its string form (`1198.795e2`), so a binary-float artefact such as
 * 1198.795 × 100 = 119879.49999999999 cannot round the wrong way.
 */
export function roundToMinorUnit(value: number, currency: TerritoryCurrency): number {
  const decimals = CURRENCY_MINOR_UNITS[currency];
  const text = String(value);
  if (text.includes('e')) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }
  return Number(`${Math.round(Number(`${text}e${decimals}`))}e-${decimals}`);
}

/** VAT on one vehicle's net monthly amount, rounded to the cent as the line shows it; 0 when exempt. */
export function vatPerVehicle(net: number, vatRatePercent: number, vatExempt: boolean): number {
  return vatExempt ? 0 : roundToCents((net * vatRatePercent) / 100);
}

/**
 * A contract's vehicle count and monthly amounts (SWOOP-349). One rule for the wizard's summary and for
 * the server, which stores the result on the contract.
 *
 * VAT is worked out per vehicle and rounded to the cent, as the line shows it; a line's total is its
 * quantity times the amount including VAT. Every line counts towards `vehicleCount`; a line with no
 * invoiced amount yet adds nothing to the amounts. An exempt contract carries no VAT.
 */
export function computeContractTotals(
  lines: ReadonlyArray<Pick<ContractLine, 'invoicedMonthlyNet' | 'quantity'>>,
  vatRatePercent: number,
  vatExempt: boolean,
): ContractTotals {
  const totals: ContractTotals = { monthlyGross: 0, monthlyNet: 0, monthlyVat: 0, vehicleCount: 0 };
  for (const { invoicedMonthlyNet, quantity } of lines) {
    totals.vehicleCount += quantity;
    if (invoicedMonthlyNet === null) {
      continue;
    }
    const vat = vatPerVehicle(invoicedMonthlyNet, vatRatePercent, vatExempt);
    totals.monthlyNet += invoicedMonthlyNet * quantity;
    totals.monthlyVat += vat * quantity;
  }
  totals.monthlyNet = roundToCents(totals.monthlyNet);
  totals.monthlyVat = roundToCents(totals.monthlyVat);
  totals.monthlyGross = roundToCents(totals.monthlyNet + totals.monthlyVat);
  return totals;
}

/**
 * A contract's value (SWOOP-343, SWOOP-346): what the customer pays over the term, VAT included — the base
 * of the per-mille and percent extra fees. Each line counts `quantity × amount including VAT × its own
 * term`; a line without an invoiced amount or a term adds nothing yet.
 */
export function computeContractValue(
  lines: ReadonlyArray<Pick<ContractLine, 'invoicedMonthlyNet' | 'quantity' | 'termMonths'>>,
  vatRatePercent: number,
  vatExempt: boolean,
): number {
  let value = 0;
  for (const { invoicedMonthlyNet, quantity, termMonths } of lines) {
    if (invoicedMonthlyNet === null || termMonths === null) {
      continue;
    }
    const gross = invoicedMonthlyNet + vatPerVehicle(invoicedMonthlyNet, vatRatePercent, vatExempt);
    value += quantity * gross * termMonths;
  }
  return roundToCents(value);
}
